import { useEffect, useState } from 'react';
import { endpoints } from '../../api';
import type { SpeechOrder } from '../../api/speech';

export interface PhotoPreview {
  index: number;
  url: string;
  name: string;
  mime: string;
  blob: Blob;
}
/** Загружаем варианты только через owner-protected endpoint, ссылки поставщика не используются. */
/** limit — сколько снимков может быть в заказе (фото — до 4, фотосессии и наборы — больше). */
export function usePhotoGallery(order: SpeechOrder | null, limit = 48) {
  const [state, setState] = useState<{ identity: string; items: PhotoPreview[]; error: boolean }>({
    identity: '',
    items: [],
    error: false,
  });
  const [attempt, retry] = useState(0);
  const identity = order?.status === 'done' && order.result?.available ? `${order.id}:${order.result_revision}:${attempt}:${limit}` : '';
  useEffect(() => {
    if (!identity || !order?.result) return;
    const request = new AbortController();
    const current: string[] = [];
    let disposed = false;
    const timer = setTimeout(() => {
      request.abort();
      current.splice(0).forEach(url => URL.revokeObjectURL(url));
      if (!disposed) setState({ identity, items: [], error: true });
    }, 30000);
    void (async () => {
      const assets = order.result!.assets.filter(asset => /^image\/(jpeg|png|webp)$/.test(asset.mime));
      if (!assets.length || assets.length > limit) throw new Error('photo_result_invalid');
      const items: PhotoPreview[] = [];
      for (const asset of assets) {
        const blob = await endpoints.speech.result(order, asset.index, request.signal);
        if (request.signal.aborted || disposed) return;
        if (!/^image\/(jpeg|png|webp)$/.test(blob.type) || blob.size > 30 * 1024 * 1024) throw new Error('photo_result_invalid');
        const url = URL.createObjectURL(blob);
        current.push(url);
        items.push({ index: asset.index, url, name: asset.file_name, mime: blob.type, blob });
      }
      if (!disposed && !request.signal.aborted) {
        setState({ identity, items, error: false });
      }
    })()
      .catch(() => {
        current.splice(0).forEach(url => URL.revokeObjectURL(url));
        if (!disposed) setState({ identity, items: [], error: true });
      })
      .finally(() => clearTimeout(timer));
    return () => {
      disposed = true;
      request.abort();
      clearTimeout(timer);
      current.forEach(url => URL.revokeObjectURL(url));
    };
  }, [identity, order, limit]);
  return {
    items: state.identity === identity ? state.items : [],
    loading: !!identity && state.identity !== identity,
    error: !!identity && state.identity === identity && state.error,
    retry: () => retry(value => value + 1),
  };
}
